/**
 * PillarCard — shared "Maison-style" card across every pillar
 * (founder ruling 1 Jul 2026 evening):
 *
 *   "I love the front-facing card of Maison/Exclusive — why can't
 *    /table /stay /fit /care all be like this? View + Send a brief
 *    at the bottom, atmosphere chips and details inside the drawer."
 *
 * This component is the single source of truth for the pillar card
 * surface. Each pillar passes its own data shape via props; the card
 * renders identical chrome and emits two CTAs:
 *
 *   • View         — opens the drawer (atmosphere, reviews, map, full info)
 *   • Send a brief — opens the concierge brief modal
 *
 * Optional photo top-pills (e.g. OPEN/CLOSED on /care, primary_type
 * label on /stay) can be supplied via the `topRightPill` / `topLeftPill`
 * render props.
 *
 * Photo URLs are resolved against the backend (proxied via
 * /api/places/photo) — no AIza key ever lives in the client.
 */
import React from "react";
import { fireSelfServeIntent } from "@/lib/self-serve-intent";

const API = process.env.REACT_APP_BACKEND_URL || "";

const T = {
  bg:    "#F4EFE6",
  card:  "#FCFAF4",
  ink:   "#211C15",
  ink2:  "#5C5447",
  ink3:  "#8A8071",
  line:  "#E5DCCB",
  line2: "#D8CDB7",
  brass: "#8B6914",
};

function resolveSrc(u) {
  if (!u) return null;
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  return `${API}${u.startsWith("/") ? "" : "/"}${u}`;
}

export default function PillarCard({
  /** Data */
  venue,                   // { name, photo_url, short_address, address_line, city_name, rating, user_ratings_total }
  /** Behaviour */
  onView,                  // () => void — opens drawer
  onBrief,                 // () => void — opens brief modal
  /** Optional pills overlayed on the photo */
  topLeftPill,             // ReactNode (e.g. "DENTAL CLINIC" badge)
  topRightPill,            // ReactNode (e.g. "OPEN" / "$$$")
  /** Optional badge bottom-left of photo (e.g. "24h · URGENT") */
  photoBadge,              // ReactNode
  /** Optional external direct-reserve link (founder ruling Jul 2026:
   *  surface OpenTable / Booking.com / florist's checkout when present
   *  · highest-intent path · zero clutter when absent). */
  reserveUrl,              // string · external URL
  reserveLabel,            // string · default "Reserve ↗"
  /** Pillar benefit key ("table" | "stay" | "fit" | "care" | "airport" | ...)
   *  When set alongside `reserveUrl`, every Reserve-direct click also
   *  fires the FE audit + MC courtesy ping (03 Jul 2026 fix — the pill
   *  was previously a plain `<a>` with no telemetry, so real member
   *  Reserve clicks were invisible to LC and to MC's Zoho). */
  benefit,                 // string
  /** testid prefix · `<prefix>-card`, `<prefix>-card-view`, `<prefix>-card-brief` */
  testidPrefix = "pillar",
  /** Label for the brief button · default "Send a brief" */
  briefLabel = "Send a brief",
  viewLabel  = "View",
}) {
  const photoSrc = resolveSrc(venue?.photo_url);
  const rating = venue?.rating ? Number(venue.rating) : null;
  const ratingCount = venue?.user_ratings_total;
  return (
    <article
      data-testid={`${testidPrefix}-card`}
      style={{
        background: T.card,
        border: `1px solid ${T.line}`,
        borderRadius: 16,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(33,28,21,0.10)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)";  e.currentTarget.style.boxShadow = "none"; }}
    >
      <div
        data-testid={`${testidPrefix}-card-photo`}
        onClick={onView}
        style={{
          aspectRatio: "4 / 3",
          position: "relative",
          cursor: onView ? "pointer" : "default",
          background: photoSrc
            ? `linear-gradient(180deg, rgba(33,28,21,0) 55%, rgba(33,28,21,0.18) 100%), url(${photoSrc}) center/cover no-repeat`
            : `linear-gradient(135deg, ${T.line} 0%, ${T.line2} 60%, ${T.brass}33 100%)`,
        }}
      >
        {!photoSrc && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Fraunces', 'EB Garamond', serif",
            fontSize: 30, fontWeight: 500, color: T.brass,
          }}>
            {(venue?.name || "C").trim().charAt(0).toUpperCase()}
          </div>
        )}
        {topLeftPill && (
          <span style={{ position: "absolute", top: 12, left: 12 }}>
            {topLeftPill}
          </span>
        )}
        {topRightPill && (
          <span style={{ position: "absolute", top: 12, right: 12 }}>
            {topRightPill}
          </span>
        )}
        {photoBadge && (
          <span style={{ position: "absolute", bottom: 12, left: 12 }}>
            {photoBadge}
          </span>
        )}
        {reserveUrl && (
          <a
            href={reserveUrl}
            target="_blank"
            rel="noreferrer noopener"
            data-testid={`${testidPrefix}-card-reserve`}
            onClick={(e) => {
              e.stopPropagation();
              // FE fire-audit + MC courtesy ping (03 Jul 2026 fix).
              // Never blocks · never throws · anchor still opens as
              // normal. Silently no-ops for non-MC traffic downstream.
              if (benefit) {
                try {
                  fireSelfServeIntent({ benefit, venue });
                } catch { /* swallow · audit is courtesy */ }
              }
            }}
            style={{
              position: "absolute", bottom: 12, right: 12,
              background: T.brass, color: T.bg,
              padding: "5px 11px", borderRadius: 999,
              fontFamily: "ui-monospace, monospace",
              fontSize: 10.5, letterSpacing: "0.08em",
              fontWeight: 700, textDecoration: "none",
              boxShadow: "0 4px 12px rgba(139,105,20,0.32)",
            }}
          >
            {reserveLabel || "Reserve ↗"}
          </a>
        )}
      </div>
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
        <h3 className="t-serif" style={{ fontSize: 17, fontWeight: 500, lineHeight: 1.22, margin: 0, color: T.ink }}>
          {venue?.name || "Untitled"}
        </h3>
        <p style={{ fontSize: 11.5, color: T.ink3, margin: 0, lineHeight: 1.4 }}>
          {venue?.short_address || venue?.address_line || venue?.city_name || "—"}
        </p>
        {venue?.whisper && (
          <p
            data-testid={`${testidPrefix}-card-whisper`}
            style={{
              fontSize: 12.5, color: T.ink2, margin: "4px 0 0",
              fontStyle: "italic", lineHeight: 1.45,
              fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
            }}
          >
            &ldquo;{venue.whisper}&rdquo;
          </p>
        )}
        {rating && (
          <p
            data-testid={`${testidPrefix}-card-rating`}
            style={{ fontSize: 12, color: T.ink2, margin: 0, display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}
          >
            <span style={{ color: T.brass, fontWeight: 700 }}>★</span>
            <span style={{ fontWeight: 600, color: T.ink }}>{rating.toFixed(1)}</span>
            {ratingCount ? (
              <span style={{ color: T.ink3 }}>({ratingCount.toLocaleString()})</span>
            ) : null}
          </p>
        )}
        <div style={{ marginTop: "auto", display: "flex", gap: 8, paddingTop: 12 }}>
          <button
            type="button"
            data-testid={`${testidPrefix}-card-view`}
            onClick={onView}
            style={{
              background: "transparent", color: T.ink2,
              border: `1px solid ${T.line2}`, borderRadius: 999,
              padding: "8px 14px", fontSize: 12, fontWeight: 500,
              cursor: "pointer", fontFamily: "inherit", flex: 1,
            }}
          >
            {viewLabel}
          </button>
          <button
            type="button"
            data-testid={`${testidPrefix}-card-brief`}
            onClick={onBrief}
            style={{
              background: T.ink, color: T.bg,
              border: 0, borderRadius: 999,
              padding: "8px 14px", fontSize: 12, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit", flex: 1.5,
            }}
          >
            {briefLabel}
          </button>
        </div>
      </div>
    </article>
  );
}
